import type { Layer } from '@geosoft/viewer-sdk';
import { Card, Chip, Icon, Stack, Text, tokens } from '@/design';
import {
  layerStatuses,
  providerIcon,
  providerIdFromLayer,
  providerLabel,
} from './layerModel';
import { PivotLink } from '@/components/pivots/PivotLink';
import type { PivotContext } from '@/app/pivots/pivotRegistry';
import {
  VectorToolCard,
  type VectorStyleHook,
} from '@/components/vector/VectorToolCard';
import { isVectorToolCardLayer } from '@/components/vector/vectorStyleModel';

interface LayerDetailPanelProps {
  layer: Layer | null;
  mixedCrs: boolean;
  /** Current view context for return navigation */
  viewContext?: PivotContext;
  /**
   * Engine style-hook seam consumed by the WP-4 `VectorToolCard`. When
   * omitted, the card still renders (UI-only); style and filter
   * changes are buffered but never propagate to the engine.
   */
  engine?: VectorStyleHook | null;
}

function readMetadata(layer: Layer, key: string, fallback = 'Unknown'): string {
  const value = layer.style?.[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

export function LayerDetailPanel({
  layer,
  mixedCrs,
  viewContext,
  engine,
}: LayerDetailPanelProps) {
  if (!layer) {
    return (
      <Card level={2} header="Layer detail">
        <Text variant="body" as="p" style={{ margin: 0 }}>
          Select a layer row to inspect provenance, CRS, provider, and credential health.
        </Text>
      </Card>
    );
  }

  const statuses = layerStatuses(layer, { mixedCrs, now: new Date() });
  const providerId = providerIdFromLayer(layer);
  const metadataRows: Array<{ label: string; value: string; icon: 'info' | 'history' | 'language' | 'extension' | 'key' }> = [
    { label: 'Source', value: providerLabel(providerId), icon: 'extension' },
    { label: 'Version', value: readMetadata(layer, 'version', 'v1'), icon: 'history' },
    { label: 'Last tiled', value: readMetadata(layer, 'lastTiledAt', 'Unknown'), icon: 'history' },
    {
      label: 'Timestamp',
      value: layer.temporal?.end ?? layer.temporal?.start ?? 'No temporal metadata',
      icon: 'history',
    },
    { label: 'CRS', value: layer.crs, icon: 'language' },
    { label: 'Provider', value: providerLabel(providerId), icon: 'extension' },
    {
      label: 'Credential health',
      value: readMetadata(layer, 'credentialHealth', 'Healthy'),
      icon: 'key',
    },
  ];

  return (
    <Card level={2} header={layer.label ?? layer.id}>
      <Stack direction="column" gap={2}>
        <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
          <Chip icon="layers" label={layer.kind} variant="neutral" />
          <Chip icon={providerIcon(providerId)} label={providerLabel(providerId)} variant="neutral" />
          <Chip icon="language" label={layer.crs} variant="neutral" />
          {statuses.map((status) => (
            <Chip key={status.key} icon={status.icon} label={status.label} variant={status.variant} />
          ))}
        </Stack>
        <div role="list" aria-label="Layer metadata">
          <Stack direction="column" gap={2}>
            {metadataRows.map((row) => (
              <div
                key={row.label}
                role="listitem"
                style={{
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.input,
                  padding: tokens.space[2],
                  background: tokens.color.surface[2],
                }}
              >
                <Stack direction="row" align="center" gap={2}>
                  <Icon name={row.icon} size={20} decorative />
                  <Stack direction="column" gap={1}>
                    <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
                      {row.label}
                    </Text>
                    <Text variant="body" as="span">
                      {row.value}
                    </Text>
                  </Stack>
                </Stack>
              </div>
            ))}
          </Stack>
        </div>
        
        {/* WP-4 — Vector ToolCard activates when the selected layer is a
            vector / feature-table family (vector | pmtiles | geojson |
            flatgeobuf | geoparquet | dxf | ogc-features-snapshot). */}
        {isVectorToolCardLayer(layer) ? (
          <VectorToolCard layer={layer} engine={engine ?? null} />
        ) : null}

        {/* Cross-app pivots for asset layers */}
        {layer && layer.id.includes('asset') && (
          <Stack direction="column" gap={2}>
            <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
              Cross-app actions
            </Text>
            <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
              <PivotLink
                pivotId="asset-view"
                context={{
                  resourceId: layer.id,
                  returnView: viewContext?.returnView,
                  time: viewContext?.time,
                  scenario: viewContext?.scenario,
                }}
              />
              {/* Additional pivots based on layer metadata */}
              {typeof layer.style === 'object' && layer.style && 'drillholeId' in layer.style && typeof layer.style.drillholeId === 'string' && (
                <PivotLink
                  pivotId="drillhole-collar"
                  context={{
                    resourceId: layer.style.drillholeId,
                    returnView: viewContext?.returnView,
                  }}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
